import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, Link } from 'react-router-dom'
import queryString from 'query-string'

import useQueryParams from '../../../hooks/useQueryParams'

import useDateRangeFilter from '../../../hooks/useDateRangeFilter'
import DateRangeFilter from '../../Reports/Filters/DateRangeFilter/DateRangeFilter'

import AdminPicker from '../../../components/AdminPicker/AdminPicker'
import CampaignSearch from '../../../components/CampaignSearch/CampaignSearch'
import useCallApi from '../../../hooks/useCallApi'
import useDownloadCSV from '../../../hooks/useDownloadCSV'
import Affiliate from '../../../templates/Affiliate/Affiliate'
import Campaign from '../../../templates/Campaign/Campaign'
import Dollars from '../../../templates/Dollars/Dollars'
import DownloadCSV from '../../../templates/DownloadCSV/DownloadCSV'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Loading from '../../../templates/UI/Loading/Loading'
import { useAuth, useProtectedPage } from '../../../context/AuthContext'
import Number from '../../../templates/Number/Number'
import absNumberSort from '../../../helpers/absNumberSort'
import callAPI from '../../../helpers/callAPI'

export default function AffComparisonReport({ campaignId, advertiserId, type = 'campaign', showSystemMessage, useParentHeader = false, periodOneStartDate, periodOneEndDate, periodTwoStartDate, periodTwoEndDate, refreshToken = 0, setAffiliate, setCampaign, setAdvertiser, setCSVFilename: setParentCSVFilename, setCSVHeaderLabels: setParentCSVHeaderLabels, setCSVDataLabels: setParentCSVDataLabels, setCSVReport: setParentCSVReport }) {
    document.title = 'Affiliate Comparison Report'

    const location = useLocation()
    const history = useHistory()

    const { initStartDate, initEndDate, selectedStartDate, selectedEndDate, quickDate, handleChangeStart, handleChangeEnd, handleQuickDate, initStartDate2, initEndDate2, selectedStartDate2, selectedEndDate2, quickDate2, handleChangeStart2, handleChangeEnd2, handleQuickDate2 } = useDateRangeFilter()
    const { adminId, setAdminId, buildUrl } = useQueryParams()

    const reportType = type === 'advertiser' ? 'advertiser' : 'campaign'

    const { data: nameData, dataLoaded: nameDataLoaded } = useCallApi(type === 'advertiser' ? `advertiser/${advertiserId}` : `campaign/${campaignId}`, 'GET', {}, [advertiserId, campaignId])

    const refreshReport = (e) => {
        // We want the URL in the url bar to update when refreshing via the button, so all it does is navigate you to a new page with the SELECTED options
        e.preventDefault()

        let url = `/reports/${type === 'campaign' ? 'campaignAffComparisonReport' : 'advertiserAffComparisonReport'}?${type === 'advertiser' ? `advertiserId=${advertiserId}&` : `campaignId=${campaignId}&`}startDate=${selectedStartDate}&endDate=${selectedEndDate}&startDate2=${selectedStartDate2}&endDate2=${selectedEndDate2}`

        history.push(url)
    }

    let url = `reports/custom/affiliateComparisonReport/${reportType}?${type === 'advertiser' ? `advertiserId=${advertiserId}&` : `campaignId=${campaignId}&`}startDate=${initStartDate}&endDate=${initEndDate}&startDate2=${initStartDate2}&endDate2=${initEndDate2}`

    // adminId= when no admin is selected, should be adminId=0, instead

    let fetchUrl = buildUrl(url)

    const { data, dataLoaded, refetch } = useCallApi(fetchUrl, 'GET', {}, [location])

    const { CSVFilename, setCSVFilename, CSVHeaderLabels, setCSVHeaderLabels, CSVDataLabels, setCSVDataLabels, CSVReport, setCSVReport } = useDownloadCSV()
    const setCSVFilenameTarget = setParentCSVFilename || setCSVFilename
    const setCSVHeaderLabelsTarget = setParentCSVHeaderLabels || setCSVHeaderLabels
    const setCSVDataLabelsTarget = setParentCSVDataLabels || setCSVDataLabels
    const setCSVReportTarget = setParentCSVReport || setCSVReport

    useEffect(() => {
        data.affiliate && setAffiliate && setAffiliate(data.affiliate)
        data.campaign && setCampaign && setCampaign(data.campaign)
        data.advertiser && setAdvertiser && setAdvertiser(data.advertiser)

        let showProjectedMargin = moment().isBefore(moment(selectedEndDate2)) && moment().isAfter(moment(selectedStartDate2))

        setCSVHeaderLabelsTarget([
            'Affiliate ID',
            'Affiliate Name',
            'P1 Clicks',
            'P2 Clicks',
            'Change Clicks',
            'P1 Conversions',
            'P2 Conversions',
            'Change Conversions',
            'P1 Net Earnings',
            'P2 Net Earnings',
            'Change Net Earnings',
            'P1 Margin',
            'P2 Margin',
            'Change Margin',
            ...(showProjectedMargin ? ['P2 Margin (Projected)'] : []),
        ])
        setCSVDataLabelsTarget([
            'affiliate_id',
            'affiliate_name',
            'periodOne.clicks',
            'periodTwo.clicks',
            'change.clicks',
            'periodOne.conversions',
            'periodTwo.conversions',
            'change.conversions',
            'periodOne.net_earnings',
            'periodTwo.net_earnings',
            'change.net_earnings',
            'periodOne.margin',
            'periodTwo.margin',
            'change.margin',
            ...(showProjectedMargin ? ['periodTwo.projected_margin'] : [])
        ])
        setCSVReportTarget(data.report || [])
        setCSVFilenameTarget(`Affiliate Comparison Report - P1: ${initStartDate}-${initEndDate} - P2: ${initStartDate2}-${initEndDate2}`)
    }, [data, setCSVDataLabelsTarget, setCSVFilenameTarget, setCSVHeaderLabelsTarget, setCSVReportTarget])

    useEffect(() => {
        const fetchEntityContext = async () => {
            if (type === 'campaign' && campaignId > 0 && setCampaign) {
                const campaignResult = await callAPI(`campaign/${campaignId}`, 'GET')
                if (campaignResult.success && campaignResult.campaign) {
                    setCampaign({
                        id: campaignResult.campaign.id,
                        name: campaignResult.campaign.name,
                        status: campaignResult.campaign.status || { value: '', name: '' },
                        manager: campaignResult.campaign.manager || { id: 0, name: '' },
                    })
                }
            }
        }

        fetchEntityContext()
    }, [campaignId, advertiserId, type, setCampaign, setAdvertiser])

    useEffect(() => {
        if (refreshToken === 0) return
        refetchReportRef.current()
    }, [refreshToken])

    const getReportOutput = (data) => {
        const reportRows = Array.isArray(data?.report) ? data.report : []
        const toSafeNumber = (value) => {
            const parsedValue = window.Number.parseFloat(value)
            return window.Number.isFinite(parsedValue) ? parsedValue : 0
        }
        const sumBy = (getValue) => reportRows.reduce((total, row) => total + toSafeNumber(getValue(row)), 0)

        let totals = [
            { type: 'none', value: '' },
            { type: 'number', value: sumBy((row) => row?.periodOne?.clicks) },
            { type: 'number', value: sumBy((row) => row?.periodTwo?.clicks) },
            { type: 'number', value: sumBy((row) => row?.change?.clicks) },
            { type: 'number', value: sumBy((row) => row?.periodOne?.conversions) },
            { type: 'number', value: sumBy((row) => row?.periodTwo?.conversions) },
            { type: 'number', value: sumBy((row) => row?.change?.conversions) },
            { type: 'dollar', value: sumBy((row) => row?.periodOne?.net_earnings) },
            { type: 'dollar', value: sumBy((row) => row?.periodTwo?.net_earnings) },
            { type: 'dollar', value: sumBy((row) => row?.change?.net_earnings) },
            { type: 'dollar', value: sumBy((row) => row?.periodOne?.margin) },
            { type: 'dollar', value: sumBy((row) => row?.periodTwo?.margin) },
            { type: 'dollar', value: sumBy((row) => row?.change?.margin) },
        ]

        let showProjectedMargin = moment().isBefore(moment(selectedEndDate2)) && moment().isAfter(moment(selectedStartDate2))

        if (showProjectedMargin) {
            totals.push({ type: 'dollar', value: sumBy((row) => row?.periodTwo?.projected_margin) })
        }

        let columns = [
            {
                Header: 'Affiliate ID',
                accessor: 'affiliate_id',
            },
            {
                Header: 'Affiliate Name',
                accessor: 'affiliate_name',
                Cell: ({ row }) => {
                    return <Affiliate aid={row.original.affiliate_id} name={row.original.affiliate_name} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P1 Clicks</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodOne.clicks',
                Cell: ({ cell: { value } }) => {
                    return <Number value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P2 Clicks</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodTwo.clicks',
                Cell: ({ cell: { value } }) => {
                    return <Number value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Change Clicks</div>,
                headerClassName: 'text-right',
                className: 'text-right border-right',
                accessor: 'change.clicks',
                sortType: absNumberSort,
                Cell: ({ cell: { value } }) => {
                    let classes = ''
                    classes += value < 0 ? 'text-danger' : value > 0 ? 'text-success' : ''
                    return <span className={classes}><Number value={value} /></span>
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P1 Conversions</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodOne.conversions',
                Cell: ({ cell: { value } }) => {
                    return <Number value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P2 Conversions</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodTwo.conversions',
                Cell: ({ cell: { value } }) => {
                    return <Number value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Change Conversions</div>,
                headerClassName: 'text-right ',
                className: 'text-right border-right',
                accessor: 'change.conversions',
                sortType: absNumberSort,
                Cell: ({ cell: { value } }) => {
                    let classes = ''
                    classes += value < 0 ? 'text-danger' : value > 0 ? 'text-success' : ''

                    return <span className={classes}><Number value={value} /></span>
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P1 Net Earnings</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodOne.net_earnings',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P2 Net Earnings</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodTwo.net_earnings',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Change Net Earnings</div>,
                headerClassName: 'text-right',
                className: 'text-right border-right',
                accessor: 'change.net_earnings',
                sortType: absNumberSort,
                Cell: ({ cell: { value } }) => {
                    let classes = ''
                    classes += value < 0 ? 'text-danger' : value > 0 ? 'text-success' : ''

                    return (
                        <div className={classes}>
                            <Dollars value={value} />
                        </div>
                    )
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P1 Margin</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodOne.margin',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P2 Margin</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodTwo.margin',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>Change Margin</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'change.margin',
                sortType: absNumberSort,
                sortDescFirst: true,
                Cell: ({ cell: { value } }) => {
                    let classes = ''
                    classes += value < 0 ? 'text-danger' : value > 0 ? 'text-success' : ''

                    return (
                        <div className={classes}>
                            <Dollars value={value} />
                        </div>
                    )
                },
            },
            {
                Header: <div style={{ textAlign: 'right' }}>P2 Margin (Projected)</div>,
                headerClassName: 'text-right',
                className: 'text-right',
                accessor: 'periodTwo.projected_margin',
                sortType: absNumberSort,
                hide: !showProjectedMargin,
                Cell: ({ cell: { value } }) => {
                    return (
                        <div className='text-secondary'>
                            <Dollars value={value} />
                        </div>
                    )
                },
            },
        ]

        return (
            <React.Fragment>
                <ReportTable data={reportRows} totals={totals} columns={columns} defaultSortBy={[{ id: 'change.margin', desc: true }]} />
            </React.Fragment>
        )
    }

    return (
        <div>
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-body p-0">
                            {!useParentHeader && (
                                <div className="d-flex justify-content-between p-3">
                                    <div className="d-flex">
                                        <div className="mr-4 d-flex align-items-center">
                                            <h1 className="mb-h1-dark mr-2">Affiliate Comparison Report</h1>
                                        </div>
                                    </div>
                                    <div>
                                        <DownloadCSV filename={CSVFilename} headerLabels={CSVHeaderLabels} data={CSVReport} dataLabels={CSVDataLabels} />
                                    </div>
                                </div>
                            )}
                            <div className="d-flex border-top flex-wrap">
                            </div>
                            <div className="d-flex">
                                <div className="m-3 d-flex align-items-center">
                                    <span className="mr-2">Date range previous period (P1):</span>
                                    <DateRangeFilter quickDate={quickDate} selectedStartDate={selectedStartDate} selectedEndDate={selectedEndDate} handleChangeStart={handleChangeStart} handleChangeEnd={handleChangeEnd} handleQuickDate={handleQuickDate} />
                                </div>
                                <div className="m-3 d-flex align-items-center">
                                    <span className="mr-2">Date range for subsequent period (P2):</span>
                                    <DateRangeFilter quickDate={quickDate2} selectedStartDate={selectedStartDate2} selectedEndDate={selectedEndDate2} handleChangeStart={handleChangeStart2} handleChangeEnd={handleChangeEnd2} handleQuickDate={handleQuickDate2} />
                                </div>
                                <div className="m-3 d-flex align-items-center">
                                    <button onClick={refreshReport} className="btn btn-sm btn-primary">
                                        Refresh Report
                                    </button>
                                </div>
                            </div>
                            {nameDataLoaded && nameData.success && (
                                <div className="alert alert-info d-inline-block mx-3">
                                    {type === 'campaign' ? 'Campaign' : 'Advertiser'}: <Link to={type === 'campaign' ? `/campaign/${campaignId}` : `/advertiser/${advertiserId}`}> {type === 'campaign' ? nameData.campaign.name : nameData.general_info.name}</Link>
                                </div>
                            )}
                            <div>{dataLoaded ? (Array.isArray(data?.report) && data.report.length > 0) ? getReportOutput(data) : <div className="p-3">No stats for the current parameters.</div> : <Loading></Loading>}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
